/**
 * Skins shop input — per-tank visual customization navigation and application.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../../input';
import type { GameState } from '../../types';
import type { GameEffect } from '../effects';
import { transitionPhase } from '../effects';
import { justPressed } from '../input-edge';
import { getAllTankIds, lookupTank } from '../../tanks';
import { setSkinProperty, resetSkin, BODY_SHAPE_IDS, NUB_TYPE_IDS, SKIN_COLORS, BORDER_STYLES, TITLES, TITLE_COLORS } from '../../skins/storage';
import { playSound } from '../../audio';

const SKIN_SECTION_COUNTS = [BODY_SHAPE_IDS.length, NUB_TYPE_IDS.length, SKIN_COLORS.length, BORDER_STYLES.length, TITLES.length, TITLE_COLORS.length, 1]; // body, nub, color, border, title, title color, reset
const SKIN_SECTION_TOTAL = 7;

export function handleSkinsShopInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  const tankIds = getAllTankIds();

  // Navigate tank row (left/right)
  if (justPressed(Keys.Q, input)) {
    state.skinsShopTank = (state.skinsShopTank - 1 + tankIds.length) % tankIds.length;
    state.skinsShopSection = 0;
    state.skinsShopItem = 0;
    playSound('menu_nav');
  }
  if (justPressed(Keys.E, input)) {
    state.skinsShopTank = (state.skinsShopTank + 1) % tankIds.length;
    state.skinsShopSection = 0;
    state.skinsShopItem = 0;
    playSound('menu_nav');
  }

  // Navigate sections (up/down)
  if (justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
    if (state.skinsShopSection > 0) {
      state.skinsShopSection--;
      state.skinsShopItem = Math.min(state.skinsShopItem, SKIN_SECTION_COUNTS[state.skinsShopSection] - 1);
      playSound('menu_nav');
    }
  }
  if (justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
    if (state.skinsShopSection < SKIN_SECTION_TOTAL - 1) {
      state.skinsShopSection++;
      state.skinsShopItem = Math.min(state.skinsShopItem, SKIN_SECTION_COUNTS[state.skinsShopSection] - 1);
      playSound('menu_nav');
    }
  }

  // Navigate items within section (left/right)
  if (justPressed(Keys.ArrowLeft, input) || justPressed(Keys.A, input)) {
    const count = SKIN_SECTION_COUNTS[state.skinsShopSection];
    if (count > 1) {
      state.skinsShopItem = (state.skinsShopItem - 1 + count) % count;
      playSound('menu_nav');
    }
  }
  if (justPressed(Keys.ArrowRight, input) || justPressed(Keys.D, input)) {
    const count = SKIN_SECTION_COUNTS[state.skinsShopSection];
    if (count > 1) {
      state.skinsShopItem = (state.skinsShopItem + 1) % count;
      playSound('menu_nav');
    }
  }

  // Confirm selection (apply skin change)
  if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
    playSound('menu_select');
    const selectedTankId = tankIds[state.skinsShopTank] ?? 'mortar';

    if (state.skinsShopSection === 0) {
      // Body shape
      const shapeId = BODY_SHAPE_IDS[state.skinsShopItem];
      if (shapeId === selectedTankId) {
        // Remove override (default)
        removeSkinProp(selectedTankId, 'bodyShape');
      } else {
        setSkinProperty(selectedTankId, 'bodyShape', shapeId);
      }
    } else if (state.skinsShopSection === 1) {
      // Nub type
      const nubId = NUB_TYPE_IDS[state.skinsShopItem];
      if (nubId === selectedTankId) {
        removeSkinProp(selectedTankId, 'nubType');
      } else {
        setSkinProperty(selectedTankId, 'nubType', nubId);
      }
    } else if (state.skinsShopSection === 2) {
      // Color
      const c = SKIN_COLORS[state.skinsShopItem];
      if (c) {
        const defaultColor = lookupTank(selectedTankId).visual.bodyColor;
        if (c.color === defaultColor) {
          removeSkinProp(selectedTankId, 'color');
        } else {
          setSkinProperty(selectedTankId, 'color', c.color);
        }
      }
    } else if (state.skinsShopSection === 3) {
      // Border style
      const b = BORDER_STYLES[state.skinsShopItem];
      if (b) {
        if (b.id === 'none') {
          removeSkinProp(selectedTankId, 'borderStyle');
        } else {
          setSkinProperty(selectedTankId, 'borderStyle', b.id);
        }
      }
    } else if (state.skinsShopSection === 4) {
      // Title
      const t = TITLES[state.skinsShopItem];
      if (t) {
        if (t.id === 'none') {
          removeSkinProp(selectedTankId, 'title');
        } else {
          setSkinProperty(selectedTankId, 'title', t.id);
        }
      }
    } else if (state.skinsShopSection === 5) {
      // Title color
      const tc = TITLE_COLORS[state.skinsShopItem];
      if (tc) {
        if (tc.rainbow) {
          setSkinProperty(selectedTankId, 'titleRainbow', true);
          removeSkinProp(selectedTankId, 'titleColor');
        } else {
          removeSkinProp(selectedTankId, 'titleRainbow');
          setSkinProperty(selectedTankId, 'titleColor', tc.color);
        }
      }
    } else if (state.skinsShopSection === 6) {
      // Reset all overrides for this tank
      resetSkin(selectedTankId);
    }
  }

  // Back
  if (justPressed(Keys.Escape, input)) {
    transitionPhase(state, 'mode_select', fx);
  }
}

/** Remove a single skin property (helper to avoid full reset). */
function removeSkinProp(tankId: string, prop: string): void {
  try {
    const store = JSON.parse(localStorage.getItem('geometry-blast-skins') || '{}');
    if (store[tankId]) {
      delete store[tankId][prop];
      if (Object.keys(store[tankId]).length === 0) delete store[tankId];
      localStorage.setItem('geometry-blast-skins', JSON.stringify(store));
    }
  } catch { /* ignore */ }
}
